/**
 * Determines the API base URL based on environment and runtime context.
 * 
 * Strategy:
 * 1. In production: Use VITE_API_URL if set, otherwise empty string (same-origin)
 * 2. In development:
 *    - If VITE_API_URL is explicitly set, use it
 *    - Otherwise, use empty string to leverage Vite proxy
 *    - This avoids cross-origin cookie issues and works regardless of port
 */
const getApiBaseUrl = (): string => {
  // If explicitly configured, use that
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // In development, always use relative URLs to leverage the Vite proxy
  // In production build, use empty string for same-origin requests
  return '';
};

/**
 * Builds a full API URL from a path.
 * Handles both absolute and relative paths correctly.
 * 
 * @param path - API path (with or without leading slash)
 * @returns Full URL or relative path for proxy
 */
const buildApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  if (!path) return baseUrl;
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // If no base URL, return the path as-is (will use proxy or same-origin)
  if (!baseUrl) return normalizedPath;
  
  // Combine base URL and path
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Gets the WebSocket/SignalR hub URL.
 * Uses the same base URL logic as API calls.
 */
const getHubUrl = (hubPath: string): string => {
  return buildApiUrl(hubPath);
};

export { getApiBaseUrl, buildApiUrl, getHubUrl };
